import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface OnlineUser {
  user_id: string;
  name: string;
  presence_ref: string;
  online_at: string;
}

interface PresenceContextType {
  onlineUsersCount: number;
  uniqueOnlineUsersCount: number;
  onlineUsers: OnlineUser[];
}

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

export function PresenceProvider({ children }: { children: ReactNode }) {
  const { user, userProfile } = useAuth();
  const [onlineUsersCount, setOnlineUsersCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);

  useEffect(() => {
    let channel: RealtimeChannel | null = null;

    if (user && userProfile) {
      channel = supabase.channel('online-users');

      channel.on('presence', { event: 'sync' }, () => {
        const newState = channel?.presenceState();
        const users: OnlineUser[] = [];

        Object.keys(newState || {}).forEach((key) => {
          const presences = newState![key] as any[];
          presences.forEach((p) => {
            users.push({
              user_id: p.user_id,
              name: p.name,
              presence_ref: p.presence_ref,
              online_at: p.online_at || new Date().toISOString(),
            });
          });
        });

        setOnlineUsers(users);
        setOnlineUsersCount(users.length);
      });

      channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel?.track({
            user_id: user.id,
            name: userProfile.nome,
            online_at: new Date().toISOString(),
          });
        }
      });
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, userProfile]);

  const uniqueOnlineUsersCount = new Set(onlineUsers.map(u => u.user_id)).size;

  const value = { onlineUsersCount, onlineUsers, uniqueOnlineUsersCount };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresence() {
  const context = useContext(PresenceContext);
  if (context === undefined) {
    throw new Error('usePresence deve ser usado dentro de um PresenceProvider');
  }
  return context;
}